import { motion } from 'motion/react';
import { SkeletonQuestion } from '../../../../context/types';

interface QuestionSkeletonProps {
  skeleton: SkeletonQuestion;
}

const pulseAnimation = {
  opacity: [0.4, 0.7, 0.4],
};

const pulseTransition = {
  duration: 1.5,
  repeat: Infinity,
  ease: 'easeInOut' as const,
};

function SkeletonPill() {
  return (
    <motion.div
      className="select-option-base bg-gray-200"
      animate={pulseAnimation}
      transition={pulseTransition}
    >
      <div className="h-5 w-3/4 rounded bg-gray-300" />
    </motion.div>
  );
}

function SelectSkeleton({ optionCount = 4 }: { optionCount?: number }) {
  const count = Math.min(optionCount, 6);
  return (
    <div className="questionnaire-question-content">
      <div className="select-question-container">
        <div className="select-options-list">
          {Array.from({ length: count }).map((_, i) => (
            <SkeletonPill key={i} />
          ))}
        </div>
      </div>
    </div>
  );
}

function TextInputSkeleton() {
  return (
    <div className="questionnaire-question-content">
      <motion.div
        className="input-field-container bg-gray-200 rounded-lg"
        animate={pulseAnimation}
        transition={pulseTransition}
      />
    </div>
  );
}

function NumberInputSkeleton() {
  return (
    <div className="questionnaire-question-content">
      <motion.div
        className="input-field-number-container bg-gray-200 rounded-lg"
        animate={pulseAnimation}
        transition={pulseTransition}
        style={{ width: '200px', height: '56px' }}
      />
    </div>
  );
}

function DateInputSkeleton() {
  return (
    <div className="questionnaire-question-content">
      <motion.div
        className="flex flex-col items-center gap-4 w-full"
        animate={pulseAnimation}
        transition={pulseTransition}
      >
        <div className="bg-gray-200 rounded-lg" style={{ width: '200px', height: '56px' }} />
      </motion.div>
    </div>
  );
}

function NumberStepperSkeleton() {
  return (
    <div className="questionnaire-question-content">
      <motion.div
        className="flex items-center justify-center gap-4"
        animate={pulseAnimation}
        transition={pulseTransition}
      >
        <div className="w-12 h-12 bg-gray-200 rounded-full" />
        <div className="w-20 h-16 bg-gray-200 rounded-lg" />
        <div className="w-12 h-12 bg-gray-200 rounded-full" />
      </motion.div>
    </div>
  );
}

function ScrollScaleSkeleton({ type }: { type: 'height' | 'weight' | 'height-weight' }) {
  const showHeight = type === 'height' || type === 'height-weight';
  const showWeight = type === 'weight' || type === 'height-weight';

  return (
    <div className="questionnaire-question-content">
      <div className="flex flex-col gap-6">
        {showWeight && (
          <motion.div
            className="flex flex-col items-center"
            animate={pulseAnimation}
            transition={pulseTransition}
          >
            <div className="w-full max-w-[300px] h-24 bg-gray-200 rounded-lg" />
          </motion.div>
        )}
        {showHeight && (
          <motion.div
            className="flex flex-col items-center"
            animate={pulseAnimation}
            transition={pulseTransition}
          >
            <div className="w-full max-w-[300px] h-24 bg-gray-200 rounded-lg" />
          </motion.div>
        )}
      </div>
    </div>
  );
}

function ActivityMatrixSkeleton() {
  return (
    <div className="questionnaire-question-content">
      <motion.div
        className="flex flex-col gap-4 w-full"
        animate={pulseAnimation}
        transition={pulseTransition}
      >
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="flex items-center gap-4 p-4 bg-gray-200 rounded-lg">
            <div className="flex-1 h-5 bg-gray-300 rounded" />
            <div className="flex gap-2">
              <div className="w-16 h-10 bg-gray-300 rounded" />
              <div className="w-16 h-10 bg-gray-300 rounded" />
            </div>
          </div>
        ))}
      </motion.div>
    </div>
  );
}

function NameEmailSkeleton() {
  return (
    <div className="questionnaire-question-content">
      <motion.div
        className="flex flex-col gap-4 w-full"
        animate={pulseAnimation}
        transition={pulseTransition}
      >
        <div className="bg-gray-200 rounded-lg" style={{ height: '56px' }} />
        <div className="bg-gray-200 rounded-lg" style={{ height: '56px' }} />
      </motion.div>
    </div>
  );
}

export function QuestionSkeleton({ skeleton }: QuestionSkeletonProps) {
  const { questionType, optionCount } = skeleton;

  switch (questionType) {
    case 'select':
    case 'radio':
      return <SelectSkeleton optionCount={optionCount} />;

    case 'multiselect':
    case 'checkbox':
      return <SelectSkeleton optionCount={optionCount} />;

    case 'text':
      return <TextInputSkeleton />;

    case 'number':
    case 'decimal':
      return <NumberInputSkeleton />;

    case 'date':
      return <DateInputSkeleton />;

    case 'number-stepper':
      return <NumberStepperSkeleton />;

    case 'height':
      return <ScrollScaleSkeleton type="height" />;

    case 'weight':
      return <ScrollScaleSkeleton type="weight" />;

    case 'height-weight':
      return <ScrollScaleSkeleton type="height-weight" />;

    case 'exercise-activity-matrix':
      return <ActivityMatrixSkeleton />;

    case 'name-email':
      return <NameEmailSkeleton />;

    default:
      return <SelectSkeleton optionCount={4} />;
  }
}
